/**
 * Video Showcase
 * 视频展示组件 - 背景视频 + 全屏视频播放
 */

class VideoShowcase {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;

    // DOM 元素
    this.bgVideo = section.querySelector('.video-showcase__bg-video');
    this.playBtn = section.querySelector('[data-video-btn]');
    this.modal = section.querySelector('[data-video-modal]');
    this.modalVideo = section.querySelector('[data-fullscreen-video]');
    this.closeElements = section.querySelectorAll('[data-video-close]');

    this.init();
  }

  init() {
    // 确保背景视频自动播放
    this.initBackgroundVideo();

    // 绑定按钮事件
    this.bindEvents();
  }

  /**
   * 初始化背景视频
   */
  initBackgroundVideo() {
    if (!this.bgVideo) return;

    // 确保背景视频静音并循环
    this.bgVideo.muted = true;
    this.bgVideo.loop = true;
    this.bgVideo.playsInline = true;
    this.bgVideo.autoplay = true;

    // 确保视频加载
    this.bgVideo.load();

    // 监听视频可以播放事件
    this.bgVideo.addEventListener('canplay', () => {
      this.attemptPlay();
    }, { once: true });

    // 立即尝试播放
    this.attemptPlay();

    // 使用 Intersection Observer 确保视频在可见时播放
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.attemptPlay();
          }
        });
      }, { threshold: 0.1 });

      observer.observe(this.section);
      this.videoObserver = observer;
    }
  }

  /**
   * 尝试播放视频
   */
  attemptPlay() {
    if (!this.bgVideo) return;

    const playPromise = this.bgVideo.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('背景视频播放成功');
        })
        .catch(error => {
          console.log('背景视频自动播放被阻止:', error);

          // 用户交互后播放
          const playOnInteraction = () => {
            this.bgVideo.play()
              .then(() => console.log('用户交互后视频播放成功'))
              .catch(e => console.log('用户交互后视频播放失败:', e));
          };

          document.addEventListener('click', playOnInteraction, { once: true });
          document.addEventListener('touchstart', playOnInteraction, { once: true });
          document.addEventListener('scroll', playOnInteraction, { once: true });
        });
    }
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    // 播放按钮点击
    if (this.playBtn) {
      this.playBtn.addEventListener('click', () => {
        this.openModal();
      });
    }

    // 关闭按钮点击
    this.closeElements.forEach(element => {
      element.addEventListener('click', () => {
        this.closeModal();
      });
    });

    // ESC 键关闭
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('is-active')) {
        this.closeModal();
      }
    });
  }

  /**
   * 打开模态框
   */
  openModal() {
    if (!this.modal || !this.modalVideo) return;

    // 显示模态框
    this.modal.classList.add('is-active');

    // 禁止背景滚动
    document.body.style.overflow = 'hidden';

    // 播放全屏视频
    this.modalVideo.currentTime = 0; // 从头开始
    const playPromise = this.modalVideo.play();

    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.log('全屏视频播放失败:', error);
      });
    }

    // 暂停背景视频（可选，节省资源）
    if (this.bgVideo && !this.bgVideo.paused) {
      this.bgVideo.pause();
    }
  }

  /**
   * 关闭模态框
   */
  closeModal() {
    if (!this.modal || !this.modalVideo) return;

    // 隐藏模态框
    this.modal.classList.remove('is-active');

    // 恢复背景滚动
    document.body.style.overflow = '';

    // 停止全屏视频
    this.modalVideo.pause();
    this.modalVideo.currentTime = 0;

    // 恢复背景视频播放
    if (this.bgVideo && this.bgVideo.paused) {
      this.bgVideo.play();
    }
  }

  /**
   * 清理
   */
  destroy() {
    if (this.bgVideo) {
      this.bgVideo.pause();
    }

    if (this.modalVideo) {
      this.modalVideo.pause();
    }

    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }

    // 恢复背景滚动
    document.body.style.overflow = '';
  }
}

// ===== 初始化 =====

function initVideoShowcase() {
  const sections = document.querySelectorAll('.video-showcase');
  const instances = [];

  sections.forEach(section => {
    const instance = new VideoShowcase(section);
    instances.push(instance);
  });

  window.videoShowcaseInstances = instances;
}

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoShowcase);
} else {
  initVideoShowcase();
}

// ===== Shopify 主题编辑器支持 =====

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('.video-showcase');
    if (section) {
      new VideoShowcase(section);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('.video-showcase');
    if (section && window.videoShowcaseInstances) {
      const instance = window.videoShowcaseInstances.find(
        inst => inst.section === section
      );
      if (instance) {
        instance.destroy();
        const index = window.videoShowcaseInstances.indexOf(instance);
        window.videoShowcaseInstances.splice(index, 1);
      }
    }
  });
}